// 3rd-party modules
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// project modules
import Ripple from '../shared/additionalComponents/Ripple';
import { RootState } from '../../stores';

// models
import { AccountKiboshDevice } from '../../models/accountKiboshDevice';
import { SubNavigationItem, TopNavigationItem } from '../../models/types/navigation';

// icons
import AccountSvg from '../../assets/icons/account.svg';
import DashSvg from '../../assets/icons/dash.svg';
import HouseSvg from '../../assets/icons/house.svg';
import ProfileSvg from '../../assets/icons/profile.svg';
import DevicesSvg from '../../assets/icons/devices-carbon.svg';
import FiltersSvg from '../../assets/icons/settings-adjust-carbon.svg';
import SupportSvg from '../../assets/icons/support.svg';
// defines
const topNavigationItems: TopNavigationItem[] = [
  {
    id: 'home',
    text: 'Home',
    icon: 'home',
    link: '/portal',
    customIcon: HouseSvg,
    mode: ["router", "vpn", "dns"]
  },
  {
    id: 'profiles',
    text: 'Profiles',
    icon: 'supervisor_account',
    link: '/portal/profiles',
    customIcon: ProfileSvg,
    mode: ["router", "dns"]
  },
  {
    id: 'devices',
    text: 'Devices',
    link: '/portal/devices',
    customIcon: DevicesSvg,
    mode: ["router"]
  },
  {
    id: 'filters',
    text: 'Filters',
    link: '/portal/filters',
    customIcon: FiltersSvg,
    mode: ["router"]
  },


  {
    id: 'dash',
    text: 'Advanced',
    icon: 'dashboard',
    link: '/portal/dash',
    customIcon: DashSvg,
    mode: ["router"]
  },
  {
    id: 'dns',
    text: 'DNS',
    icon: 'dashboard',
    link: '/portal/dns',
    customIcon: DashSvg,
    mode: ["dns"]
  },
  {
    id: 'vpn',
    text: 'Mobile',
    icon: 'vpn_lock',
    link: '/portal/vpns',
    mode: ["vpn", "dns"]
  },
  {
    id: 'account-details',
    text: 'Account',
    icon: 'verified_user',
    link: '/portal/account-details',
    customIcon: AccountSvg,
    mode: ["router", "vpn", "dns"]
  }
];

type Props = {
  onNavItemSelect?: (navItems: SubNavigationItem[]) => void;
};

export default function PortalTopNavigation({ onNavItemSelect }: Props) {
  const location = useLocation();
  const [currentSelectedItem, setCurrentSelectedItem] = useState('');
  const [visibleItems, setVisibleItems] = useState<TopNavigationItem[]>([]);
  const [currentLink, setCurrentLink] = useState('/');
  const navigate = useNavigate();

  const kiboshDevice =
    useSelector((state: RootState) => state.user.kiboshDevice) ||
    new AccountKiboshDevice();

  useEffect(() => {
    const current = `/${location.pathname.substring(1)}`;
    const selectedItems = topNavigationItems.filter(x => x.link === current || x.children?.filter(y => y.link === current).length);

    if (selectedItems.length) {
      onNavItemClick(selectedItems[0]);
    }
  }, []);

  useEffect(() => {
    setVisibleItems(
      topNavigationItems.filter(x => x.mode?.includes(kiboshDevice.kiboshDeviceTypeCode?.toLowerCase() || "router"))
    )
  }, [kiboshDevice]);

  useEffect(() => { // go back to first item if current is not a part of the list
    if (currentSelectedItem && visibleItems.length && !visibleItems.map(x=> x.text).includes(currentSelectedItem)) {
      onNavItemClick(visibleItems[0]);
      navigate(visibleItems[0].link || "/portal");
    }
  }, [visibleItems]);

  useEffect(() => {
    const url = `/${location.pathname.substring(1)}`;
    setCurrentLink(url);
    const selectedItems = topNavigationItems.filter(x => x.link === url || x.children?.filter(y => y.link === url).length);
    if (selectedItems.length) {
      setCurrentSelectedItem(selectedItems[0].text);
    }
  }, [location]);

  const isSelectedItem = (item: TopNavigationItem): Boolean => {
    return (
      currentSelectedItem &&
      currentSelectedItem === item.text
    ) ||
      (
        !currentSelectedItem &&
        (
          currentLink === item.link || !!item.children?.filter(x => x.link === currentLink).length
        )
      );
  }

  const onNavItemClick = (item: TopNavigationItem) => {
    setCurrentSelectedItem(item.text);

    if (onNavItemSelect) {
      onNavItemSelect(item.children || [])
    }
  };

  return (
    <>
      <group
        data-background="highlight"
        data-space="5">
        <group data-weight="600" data-grid-template="90" data-gap="5" data-wrap="no" data-name="navigation">
          {visibleItems.map(item =>
            <Ripple key={item.id}>
              <Link data-name="navigation_item" to={item.link || ""} data-gap="2" data-max-length="200" onClick={() => onNavItemClick(item)} data-type="group" data-contain="" data-radius="10" data-background={isSelectedItem(item) && "main"} data-ink-color={isSelectedItem(item) && "main-dark"}>
                <group data-contain="" data-interactive="" data-index="2" data-cursor="pointer" data-color={isSelectedItem(item) && "white"}>
                  <group data-direction="column" data-align="center" data-space="5" data-gap="2" >
                    {!!item.customIcon ?
                      <img src={item.customIcon} className={isSelectedItem(item) ? "selected-item-icon" : ""} alt={item.text} />
                      :
                      !!item.icon && <icon>{item.icon}</icon>
                    }
                    <text data-ellipsis="">{item.text}</text>
                  </group>
                </group>
              </Link>
            </Ripple>
          )
          }
        </group>
        <group data-height="15" data-adaptive="ios"></group>
      </group>
    </>
  );
}
